import type { Viewing, ViewingStatus } from '@bbbk/schema';
import { DogCell, Icon, StatusPill } from '../common';
import {
  breedLabel,
  formatOwnerLabel,
  formatOwnerPhone,
  derivePaymentStatus,
  stayDatesLabel,
  nightsBetween,
  displayRatePerNight,
  nightsLabel,
  nightlyRateLabel,
} from '../bookings/domain';
import { deriveBookingFlags } from '../bookings/flags';
import { VIEWING_STATUS_RECORDS } from '../bookings/constants';
import { DeskActions } from './DeskActions';
import {
  canBook,
  isSettled,
  pillTarget,
  viewingDogBreeds,
  viewingFlags,
  viewingSlotLabel,
} from './viewings';
import { fmtDate } from '../../lib/dates';
import { useMediaQuery } from '../../lib/useMediaQuery';

/**
 * How many slots the desk row's tag strip and avatar pile may take — chips and
 * counter together, faces and counter together.
 *
 * A RENDER DECISION, not a styling one, which is the bar `useMediaQuery` sets:
 * the chips cannot shrink and cannot be counted in CSS, so how many to draw has
 * to be settled before the markup exists.
 *
 * HOW MANY SLOTS THE AVATAR PILE MAY TAKE, and nothing else any more.
 *
 * It used to size the tag strip too, and that is now measured — the strip knows
 * things a breakpoint cannot, such as that this row's dog is called "Bo". What
 * a breakpoint still knows is when the cell is too narrow for two faces: below
 * 1400 the pile drops to one, carrying the dog COUNT where there is more than
 * one, and the room that saves goes to the name.
 */
function useDeskSlots(): { pile: number } {
  const stacked = useMediaQuery('(max-width: 1200px)');
  // Phrased so that `false` means 13 inches — `useMediaQuery` answers false
  // wherever `matchMedia` does not exist, and the sensible fallback is the rung
  // the desk is designed around rather than the narrowest one it has.
  const narrow = useMediaQuery('(max-width: 1400px)');
  return { pile: narrow && !stacked ? 1 : 2 };
}




import type { Booking, BookingStatus } from '../bookings/types';

interface BookingRowProps {
  kind: 'arrival' | 'collection';
  b: Booking;
  onEditBooking: (id: string) => void;
  onSetStatus: (id: string, status: BookingStatus) => void;
  onTogglePaid?: (id: string) => void;
  // Each control is disabled while ITS OWN optimistic write is in flight, so a
  // rapid second press can't race the first into an out-of-order server result.
  busyStatus?: boolean;
  busyPaid?: boolean;
}

interface ViewingRowProps {
  kind: 'viewing';
  v: Viewing;
  /** The row itself opens the viewing — the only route to No-show (AC9, AC10). */
  onOpenViewing: (v: Viewing) => void;
  onSetViewingStatus: (id: string, status: ViewingStatus) => void;
  onBook: (v: Viewing) => void;
  busyStatus?: boolean;
}

type DeskRowProps = BookingRowProps | ViewingRowProps;

// One booking on the desk. Shared by drop-offs (kind="arrival") and collections
// (kind="collection") — only the status target and balance interactivity differ.
// The dogs / owner / health block reuses the shared DogCell; the balance + status
// action cluster is the shared DeskActions. Clicking the row opens the modal.
export function DeskRow(props: DeskRowProps) {
  // A VIEWING IS NOT A BOOKING, so the branch is at the top rather than a dozen
  // ternaries down the body. It stays in THIS file because everything below the
  // branch — the cell, the flag strip, the status pill, the row chrome — is
  // shared, and a sibling component would be a second set of them to keep in
  // step.
  if (props.kind === 'viewing') return <ViewingDeskRow {...props} />;
  const { kind, b, onEditBooking, onSetStatus, onTogglePaid, busyStatus, busyPaid } = props;
  const isCollection = kind === 'collection';
  // The status action is done when the booking reaches its target status.
  const actionDone = isCollection ? b.status === 'collected' : b.status === 'arrived' || b.status === 'collected';
  // A dog on the collections desk is in residence and due to leave, so its badge
  // reads "Arrived" until it's collected — not its raw booking status (which may
  // still lag at confirmed). Drop-offs show the actual pre-arrival status.
  const displayStatus: BookingStatus = isCollection
    ? b.status === 'collected'
      ? 'collected'
      : 'arrived'
    : b.status;
  // A drop-off is done the moment the dog arrives — payment is a separate, later
  // concern and must not hold the row open. A collection still needs the balance
  // settled (paid) before it dims, since payment is due at collection. The action
  // button flips to ✓ on its own status (actionDone), independent of payment.
  const rowDone = isCollection ? actionDone && derivePaymentStatus(b) === 'paid' : actionDone;
  // ONE BREED, OR 'Mixed' — see breedLabel. The join of every distinct breed
  // that used to live here fitted while it had a column; on a subline it says
  // the first dog's breed about the whole household as soon as it truncates.
  const breed = breedLabel(b.dogs);
  // Nights (its own line under the dates) and the nightly rate — mirrors the
  // bookings table (.bt-stay-nights / .bt-due-rate) so desk and table read alike.
  const nights = nightsBetween(b.checkIn, b.checkOut);
  const rate = displayRatePerNight(b);
  const phone = formatOwnerPhone(b.ownerContacts);
  const slots = useDeskSlots();

  // Advance forward on click (undo if already at target). StatusPill stops the
  // click from bubbling to the row's modal-open, so no event handling needed.
  const onAction = () => {
    if (isCollection) onSetStatus(b.id, actionDone ? 'arrived' : 'collected');
    else onSetStatus(b.id, actionDone ? 'confirmed' : 'arrived');
  };

  return (
    <div
      className={
        'desk-row ' +
        kind +
        (rowDone ? ' done' : '') +
        (b.status === 'unconfirmed' ? ' status-unconfirmed' : '') +
        (b.status === 'requested' ? ' status-requested' : '')
      }
      onClick={() => onEditBooking(b.id)}
    >
      {/* THREE COLUMNS, EACH A STACKED PAIR (Sir, 2026-08-27). The breed and the
          rate held columns of their own and left the identity column 92px at
          1280 — an avatar, a gap, and forty pixels of name. As sublines they
          cost nothing and the two names get the room. */}
      <DogCell dogs={b.dogs} flags={deriveBookingFlags(b)} subline={breed} pileSlots={slots.pile} />
      <div className="desk-row-contact">
        <span className="desk-row-owner">{formatOwnerLabel(b.ownerContacts)}</span>
        {/* LEFT BLANK when there is no number (Sir, 2026-08-27). "No phone on
            file" is a sentence about an absence, repeated down a column where
            most households have no number on file — it filled the desk with
            words that told staff nothing they could act on. The line holds its
            height either way, so the rows still align. */}
        <span className="desk-row-phone">{phone}</span>
      </div>
      <div className="desk-row-stay">
        {/* THE LENGTH IN BRACKETS, INLINE. It was a line of its own beneath the
            dates, which spent a whole subline on two words that qualify the
            line above them — and left the rate nowhere to go but a column of
            its own. In brackets it reads as what it is: how long the range is
            (Sir, 2026-08-27). */}
        <span className="desk-row-dates mono">
          {stayDatesLabel(b.checkIn, b.checkOut)}
          <span className="desk-row-total">
            {'('}
            {/* TWO FORMS, ONE FACT, SWITCHED IN CSS. Where the column is tight
                the word goes and the letter stays — '3n' rather than nothing,
                because the count is the useful half and 'nights' is the part a
                reader supplies for themselves (Sir, 2026-08-27). Both are in
                the DOM; only one is ever displayed, so a screen reader is given
                the long form at every width. */}
            <span className="nights-long">{nightsLabel(nights)}</span>
            <span className="nights-short" aria-hidden="true">{`${nights}n`}</span>
            {')'}
          </span>
        </span>
        <span className="desk-row-nights">{rate != null ? nightlyRateLabel(rate) : ''}</span>
      </div>
      <DeskActions
        b={b}
        status={displayStatus}
        done={actionDone}
        onAction={onAction}
        onTogglePaid={() => onTogglePaid?.(b.id)}
        busyStatus={busyStatus}
        busyPaid={busyPaid}
        onOpen={() => onEditBooking(b.id)}
      />
    </div>
  );
}

// One viewing on the desk (viewing-on-the-desk). It reads like the booking rows
// around it, in the same three columns: the dogs over their breeds, the person
// over their number, the day over which half of it.
function ViewingDeskRow({ v, onOpenViewing, onSetViewingStatus, onBook, busyStatus }: ViewingRowProps) {
  // Settled EITHER WAY greys the row out, exactly as a collected dog does —
  // Visited and No-show are both outcomes, and a row that stays bright after
  // one of them is a row still asking to be dealt with.
  const done = isSettled(v);
  const slots = useDeskSlots();
  const dogBreeds = viewingDogBreeds(v.dogs);
  // "NO DOGS MENTIONED" MEANS NO DOGS. It was keyed on the BREED line being
  // empty, which is also true of a viewing that named a dog and no breed — so
  // "Milo" appeared in the cell with "No dogs mentioned" directly under it
  // (codex + claude, PR #172). Two different facts, two different sentences.
  const dogSubline =
    v.dogs.length === 0 ? 'No dogs mentioned' : dogBreeds || 'Breed not given';

  const open = () => onOpenViewing(v);

  return (
    <div
      className={'desk-row viewing' + (done ? ' done' : '')}
      onClick={(e) => {
        // NOT WHILE THE PILL IS BUSY. `StatusPill` renders `disabled` during its
        // own optimistic write, and the shared `.status-pill:disabled` rule sets
        // `pointer-events: none` — written for a READ-ONLY badge, where letting
        // the click through to the row is the point. On an in-flight control it
        // means a second press lands on the row and opens the modal nobody asked
        // for, which is the opposite of the rapid-press guard it was meant to be
        // (codex + claude, PR #172).
        if (busyStatus) return;
        if (e.target !== e.currentTarget && (e.target as HTMLElement).closest('button')) return;
        open();
      }}
      // NOT `role="button"` ANY MORE. It made every control inside the row a
      // descendant of a button, and a screen reader or voice-control user may
      // see those flattened or announced ambiguously — Book and the status pill
      // stop being separately addressable (codex, PR #172). The row keeps its
      // MOUSE affordance, which needs no role, and the keyboard gets a real
      // control of its own further down: a visually-hidden button that opens the
      // viewing. AC9 is satisfied by that button rather than by the row.
      title="Open this viewing"
    >
      {/* THE KEYBOARD'S WAY IN, and the row's accessible name. A real button —
          so it is reachable, announced and activated like one — carrying the
          only route to No-show, which lives nowhere else. It replaces the
          `role="button"` the row itself used to wear, which made Book and the
          status pill descendants of a button (AC9, and codex PR #172). */}
      <button type="button" className="sr-only" onClick={open}>
        Open the viewing for {v.contactName}
      </button>
      {/* THE SAME THREE COLUMNS AS THE BOOKING ROWS, carrying the same pairs and
          — since Sir asked for it in as many words — the same CELL: avatars,
          names, flag strip, breed beneath. A viewing's dogs are DESCRIBED
          rather than on file, so their ids are minted from the viewing's own
          and the em dash holds the place of a name nobody gave, exactly as the
          two lines already did. The Viewing tag sits where New sits, in the
          first column, because it says the same kind of thing about the row. */}
      <DogCell
        dogs={v.dogs.map((d, i) => ({ id: `${v.id}-d${i}`, name: d.name ?? '\u2014', breed: d.breed ?? null }))}
        flags={viewingFlags()}
        subline={dogSubline}
        pileSlots={slots.pile}
      />
      <div className="desk-row-contact">
        <span className="desk-row-owner">{v.contactName}</span>
        <span className="desk-row-phone">{v.phone ?? ''}</span>
      </div>
      <div className="desk-row-stay">
        <span className="desk-row-dates mono">{fmtDate(v.viewingOn)}</span>
        <span className="desk-row-nights">{viewingSlotLabel(v.slot)}</span>
      </div>
      <div className="desk-actions">
        {/* BOOK STAYS UNTIL A BOOKING ACTUALLY EXISTS (AC6). Keyed on
            `booking_id`, never on status — they may have come, gone away to
            think, and be booking now, and saving their household without going
            on to book leaves the row still owing the way through. */}
        {canBook(v) ? (
          <button
            type="button"
            // `desk-row-action` is not decoration: it is what the phone tier's
            // 44px full-width control rule selects on. Without it Book stayed a
            // small intrinsic-width button beside a status pill that had grown
            // to the mobile grid (Codex, PR #172).
            // THE DESK'S OWN OUTLINE, not the solid page primary. Arrived,
            // Collected and the Due control are all surface-filled with a
            // coloured border; `btn primary` painted over the `desk-row-action`
            // already here and made one row shout across a quiet desk (Sir,
            // 2026-08-27). `desk-row-action` is also what the phone tier's 44px
            // full-width control rule selects on, so it was always required.
            className="desk-row-action"
            onClick={(e) => {
              e.stopPropagation(); // the row's own open, not this
              onBook(v);
            }}
          >
            {/* THE GLYPH THE APP ALREADY USES FOR A STAY — the same calendar as
                "New booking" in the top bar, and the same shape as the icon on
                every status control beside it. A lone word on a row of
                icon-and-word buttons is the odd one out (Sir, 2026-08-27). */}
            <Icon name="calendar" size={13} />
            Book
          </button>
        ) : (
          /* THE MARKER, RENDERED FROM `booking_id` ALONE (book-them-in AC8) —
             never from the status, so a viewing cannot claim a booking that was
             never made. It STANDS IN THE SLOT Book left: it is pinned to the
             grid's first row like every other control, so the pill beside it
             starts at the same x whichever kind of row it is. It used to be
             rendered beside an empty placeholder that held that slot, and with
             the placeholder pinned to row one the marker fell to a second row
             beneath it — on a phone, below the row entirely (review, PR #174). */
          <span className="desk-booked" title="This viewing produced a booking">
            Booked
          </span>
        )}
        {/* The desk's own idiom: the pill advances the status and stops its
            click bubbling, the identical gesture that turns Confirmed into
            Arrived. No-show is deliberately absent here — it is the uncommon
            case, and a third control on every row would cost every viewing a
            decision in order to buy the rare one. It lives in the modal. */}
        <StatusPill
          record={VIEWING_STATUS_RECORDS[v.status]}
          onClick={() => onSetViewingStatus(v.id, pillTarget(v.status))}
          disabled={busyStatus}
          // DONE IS DONE FOR A VIEWING TOO (PR #232 review): the desk's pill is
          // teal to do and grey done, and this row knew it was settled and
          // never told the pill — a visited viewing kept the to-do colour.
          className={done ? 'is-done' : undefined}
          title={v.status === 'expected' ? 'Mark as visited' : 'Back to expected'}
        />
      </div>
    </div>
  );
}
